import axios from './apiConfig';
import { ProjetoMetricsDTO } from '../types/projetoMetrics';

/**
 * Fetch comprehensive metrics for a specific project
 *
 * Requires VIEW_ALL_PROJECTS permission (Admin/Manager roles)
 * Returns aggregated metrics including:
 * - General KPIs (total, completed, in progress, pending tasks)
 * - Completion rate and average working days
 * - Status distribution
 * - Top 10 longest tasks
 * - Metrics per collaborator
 * - Project timeline (first start date, last completion date)
 *
 * @param projetoId The ID of the project to fetch metrics for
 * @param bypassCache The backend sends Cache-Control: max-age=30 for this
 *   endpoint, so a refresh right after editing a task would get the stale
 *   response. A throwaway query param makes the browser treat it as a new URL
 *   (changing the headers instead would break CORS).
 * @returns Promise<ProjetoMetricsDTO> Complete metrics data
 * @throws Error if user doesn't have permission or project not found
 */
export const getProjetoMetricsAPI = async (
  projetoId: number,
  bypassCache = false,
): Promise<ProjetoMetricsDTO> => {
  try {
    const response = await axios.get<ProjetoMetricsDTO>(
      `/projetos/${projetoId}/metrics`,
      bypassCache ? { params: { _: Date.now() } } : undefined,
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 403) {
        throw new Error('Não tem permissão para visualizar estas métricas');
      } else if (error.response?.status === 404) {
        throw new Error('Projeto não encontrado');
      }
    }

    throw new Error(
      'Erro ao carregar métricas do projeto. Por favor, tente novamente.',
    );
  }
};
